import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bot, Trash2, RefreshCw, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { PORTAL_PRESETS, CUSTOM_PORTAL_KEY } from "./portalPresets";
import { PortalQuickLogin } from "@/components/PortalQuickLogin";

const StatusBadge = ({ conn }) => {
  if (conn.last_status === "running") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-sky-700">
        <Loader2 className="h-3 w-3 animate-spin" /> läuft ...
      </span>
    );
  }
  if (conn.last_status === "ok") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> {conn.last_message}
      </span>
    );
  }
  if (conn.last_status === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-rose-700">
        <XCircle className="h-3 w-3 shrink-0" /> {conn.last_message}
      </span>
    );
  }
  return <span className="text-[11px] text-slate-400">Noch nie abgerufen</span>;
};

const EMPTY_FORM = {
  type: "portal",
  name: "",
  business_id: "",
  ad_account_id: "",
  access_token: "",
  login_url: "",
  invoices_url: "",
  username: "",
  password: "",
};

export const ReceiptRobotSection = ({ companyId, initialPreset = null, presetKeys = null }) => {
  const [connections, setConnections] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [allPresets, setAllPresets] = useState(false);
  const pollRef = useRef(null);
  // Aus dem Bankabgleich: nur die erkannten Portale (z.B. Digitec + Galaxus) anbieten, Rest auf Wunsch
  const visiblePresets = presetKeys?.length && !allPresets ? PORTAL_PRESETS.filter((p) => presetKeys.includes(p.key)) : PORTAL_PRESETS;

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/receipt-connections`);
      setConnections(r.data.connections || []);
      return r.data.connections || [];
    } catch { return []; }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const anyRunning = connections.some((c) => c.last_status === "running");
    if (anyRunning && !pollRef.current) {
      pollRef.current = setInterval(load, 4000);
    } else if (!anyRunning && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [connections, load]);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const [preset, setPreset] = useState("");
  const presetObj = PORTAL_PRESETS.find((p) => p.key === preset) || null;

  const applyPreset = (key) => {
    if (key === "__all") { setAllPresets(true); return; }
    setPreset(key);
    const p = PORTAL_PRESETS.find((x) => x.key === key);
    setForm((f) => ({
      ...f,
      name: p ? p.name : (f.name && PORTAL_PRESETS.some((x) => x.name === f.name) ? "" : f.name),
      login_url: p ? p.login_url : "",
      invoices_url: p ? p.invoices_url : "",
    }));
  };

  const portalIncomplete = form.type === "portal" && (!form.login_url.trim() || !form.username.trim() || !form.password);

  // Aus dem Bankabgleich geöffnet («Netflix verbinden») → Formular mit Vorlage direkt aufklappen
  useEffect(() => {
    if (!initialPreset) return;
    if (initialPreset === "meta") { setShowForm(true); setForm({ ...EMPTY_FORM, type: "meta", name: "Meta Ads" }); return; }
    if (PORTAL_PRESETS.some((p) => p.key === initialPreset)) { setShowForm(true); applyPreset(initialPreset); }
  }, [initialPreset]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.post(`/companies/${companyId}/receipt-connections`, form);
      toast.success("Verbindung gespeichert");
      setForm(EMPTY_FORM);
      setPreset("");
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await apiClient.delete(`/companies/${companyId}/receipt-connections/${id}`);
      await load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const fetchNow = async (id) => {
    try {
      await apiClient.post(`/companies/${companyId}/receipt-connections/${id}/fetch-now`);
      toast.info("Abruf gestartet – neue Belege landen im Posteingang");
      setConnections((prev) => prev.map((c) => (c.id === id ? { ...c, last_status: "running" } : c)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Abruf fehlgeschlagen");
    }
  };

  const toggleAutoBook = async (id, value) => {
    try {
      const r = await apiClient.patch(`/companies/${companyId}/receipt-connections/${id}`, { auto_book: value });
      setConnections((prev) => prev.map((c) => (c.id === id ? r.data : c)));
      toast.success(value
        ? "Auto-Verbuchung aktiv: passende Belege werden ab jetzt automatisch verbucht"
        : "Auto-Verbuchung deaktiviert – Treffer bleiben Vorschläge");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Umschalten fehlgeschlagen");
    }
  };

  return (
    <div className="border-t border-slate-200 pt-3 mt-3" data-testid="receipt-robot-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
          <Bot className="h-4 w-4 text-indigo-600" /> Auto-Abruf (Roboter)
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => setShowForm((v) => !v)} data-testid="receipt-robot-toggle-form-btn">
          {showForm ? "Abbrechen" : "+ Verbindung"}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Das System loggt sich täglich (05:15) selbst ein und legt gefundene Rechnungen in den Posteingang.
        Für Meta läuft der Abruf über die offizielle API; andere Portale (Netflix, Hosting, Telecom …)
        über den Login-Roboter – funktioniert nur ohne 2FA. Rechnungsseiten ohne PDF-Download
        (z.B. Netflix) werden automatisch als A4-PDF gespeichert.
      </p>

      {showForm && (
        <form onSubmit={add} className="mt-3 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="receipt-robot-form">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Typ</Label>
              <select
                className="border border-slate-200 rounded-md px-2 py-1.5 text-sm w-full bg-white"
                value={form.type}
                onChange={(e) => set("type", e.target.value)}
                data-testid="receipt-robot-type-select"
              >
                <option value="portal">Portal mit Login (Netflix, Hosting …)</option>
                <option value="meta">Meta Ads (offizielle API)</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Name</Label>
              <Input className="bg-white" autoComplete="off" placeholder={form.type === "meta" ? "Meta Ads" : "z.B. Netflix"} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="receipt-robot-name-input" />
            </div>
          </div>

          {form.type === "meta" ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Business-ID</Label>
                  <Input className="bg-white" placeholder="z.B. 1234567890" value={form.business_id} onChange={(e) => set("business_id", e.target.value)} data-testid="receipt-robot-business-id-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Werbekonto-ID (optional)</Label>
                  <Input className="bg-white" placeholder="act_… oder Nummer" value={form.ad_account_id} onChange={(e) => set("ad_account_id", e.target.value)} data-testid="receipt-robot-ad-account-input" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">System-User Access Token</Label>
                <Input className="bg-white" type="password" placeholder="EAAG…" value={form.access_token} onChange={(e) => set("access_token", e.target.value)} data-testid="receipt-robot-token-input" />
              </div>
              <details className="text-xs text-slate-600">
                <summary className="cursor-pointer text-indigo-700">Anleitung: Token im Business Manager erstellen</summary>
                <ol className="list-decimal ml-4 mt-1 space-y-0.5">
                  <li>business.facebook.com/settings öffnen → Benutzer → <b>Systemnutzer</b> → hinzufügen</li>
                  <li>Dem Systemnutzer dein Werbekonto zuweisen + <b>Finanz-Rolle</b> (Finanzanalyst) vergeben</li>
                  <li>Deine App zuweisen → <b>Token generieren</b> mit Scopes <code>ads_read</code> + <code>business_management</code> (60 Tage)</li>
                  <li>Token hier einfügen – er wird verschlüsselt gespeichert und nie angezeigt</li>
                </ol>
              </details>
            </>
          ) : (
            <>
              <div className="space-y-1">
                <Label className="text-xs">Portal auswählen</Label>
                <select
                  className="border border-indigo-200 rounded-md px-2 py-1.5 text-sm w-full bg-white"
                  value={preset}
                  onChange={(e) => applyPreset(e.target.value)}
                  data-testid="receipt-robot-preset-select"
                >
                  <option value="">– bitte wählen –</option>
                  {visiblePresets.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                  {visiblePresets.length < PORTAL_PRESETS.length && <option value="__all">Anderes Portal … (alle anzeigen)</option>}
                  <option value={CUSTOM_PORTAL_KEY}>Anderes Portal (URLs selbst eingeben)</option>
                </select>
                {presetObj?.hint && <p className="text-[11px] text-amber-700" data-testid="receipt-robot-preset-hint">{presetObj.hint}</p>}
                {presetObj && (
                  <p className="text-[11px] text-emerald-700" data-testid="receipt-robot-preset-filled">
                    Login- und Rechnungsseite sind vorbefüllt – nur noch E-Mail und Passwort eingeben.
                  </p>
                )}
              </div>
              {(preset === CUSTOM_PORTAL_KEY || presetObj) && (
              <details className="text-xs" open={preset === CUSTOM_PORTAL_KEY}>
                <summary className="cursor-pointer text-slate-500" data-testid="receipt-robot-urls-toggle">
                  {presetObj ? "Adressen anzeigen / anpassen" : "Adressen des Portals"}
                </summary>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <div className="space-y-1">
                    <Label className="text-xs">Login-URL</Label>
                    <Input className="bg-white" placeholder="https://…/login" value={form.login_url} onChange={(e) => set("login_url", e.target.value)} data-testid="receipt-robot-login-url-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Rechnungsseite (optional)</Label>
                    <Input className="bg-white" placeholder="https://…/billing" value={form.invoices_url} onChange={(e) => set("invoices_url", e.target.value)} data-testid="receipt-robot-invoices-url-input" />
                  </div>
                </div>
              </details>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Benutzername / E-Mail</Label>
                  <Input className="bg-white" autoComplete="off" placeholder="Login beim Portal" value={form.username} onChange={(e) => set("username", e.target.value)} data-testid="receipt-robot-username-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Passwort</Label>
                  <Input className="bg-white" type="password" autoComplete="new-password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="receipt-robot-password-input" />
                </div>
              </div>
              <p className="text-[11px] text-slate-400">Zugangsdaten werden verschlüsselt gespeichert und nie im Klartext angezeigt.</p>
            </>
          )}
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={busy || portalIncomplete} title={portalIncomplete ? "Portal wählen und E-Mail + Passwort eingeben" : undefined} data-testid="receipt-robot-save-btn">
              {busy ? "Speichern ..." : "Speichern"}
            </Button>
          </div>
        </form>
      )}

      <div className="mt-2 space-y-1.5" data-testid="receipt-robot-connections-list">
        {connections.map((c) => (
          <div key={c.id} className="rounded-md border border-slate-100 px-2.5 py-2 hover:bg-slate-50" data-testid={`receipt-robot-connection-${c.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm text-slate-800 truncate">{c.name}</span>
                <span className="text-[10px] uppercase tracking-wide rounded bg-slate-100 px-1.5 py-0.5 text-slate-500 shrink-0">
                  {c.type === "meta" ? "Meta API" : "Roboter"}
                </span>
                {c.total_imported > 0 && (
                  <span className="text-[10px] text-emerald-700 shrink-0">{c.total_imported} importiert</span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  disabled={c.last_status === "running"}
                  onClick={() => fetchNow(c.id)}
                  data-testid={`receipt-robot-fetch-now-${c.id}`}
                >
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${c.last_status === "running" ? "animate-spin" : ""}`} />
                  Jetzt abrufen
                </Button>
                <button type="button" onClick={() => remove(c.id)} className="text-slate-300 hover:text-rose-600 p-1" data-testid={`receipt-robot-delete-${c.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="mt-0.5"><StatusBadge conn={c} /></div>
            {c.type === "portal" && (
              <div className="mt-1.5" data-testid={`receipt-robot-quick-login-${c.id}`}>
                <PortalQuickLogin companyId={companyId} connectionId={c.id} />
              </div>
            )}
            <div className="mt-1.5 flex items-center gap-2">
              <Switch
                checked={c.auto_book}
                onCheckedChange={(v) => toggleAutoBook(c.id, v)}
                className="scale-75 -ml-1"
                data-testid={`receipt-robot-autobook-${c.id}`}
              />
              <span className="text-[11px] text-slate-500">
                Auto-Verbuchung {c.auto_book ? "aktiv – Treffer werden sofort verbucht" : "aus – Treffer bleiben Vorschläge"}
              </span>
              {!c.auto_book && (c.total_matched || 0) >= 5 && (
                <span className="text-[11px] text-emerald-700" data-testid={`receipt-robot-autobook-hint-${c.id}`}>
                  ✨ Schon {c.total_matched} korrekte Treffer – jetzt aktivieren?
                </span>
              )}
            </div>
          </div>
        ))}
        {connections.length === 0 && (
          <p className="text-xs text-slate-400" data-testid="receipt-robot-empty">Noch keine Verbindungen eingerichtet.</p>
        )}
      </div>
    </div>
  );
};
